import {
  WEEKDAY_LABELS,
  WEEK_LABELS,
  WEEKDAY_CHART_PRESETS,
  WEEKLY_CHART_PRESETS,
  EMPTY_CHART_PRESETS
} from "./widgetConstants";

const DAY_INDEX = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

/**
 * Groups raw rate series data into chart-ready buckets for the given preset.
 * Returns [] for TODAY/YESTERDAY (empty chart).
 * If the API already returns pre-grouped chartData, pass it through directly.
 */
export const buildChartData = (series = [], preset) => {
  if (EMPTY_CHART_PRESETS.has(preset)) {
    return [];
  }

  if (WEEKDAY_CHART_PRESETS.has(preset)) {
    return buildWeekdayBuckets(series);
  }

  if (WEEKLY_CHART_PRESETS.has(preset)) {
    return buildWeeklyBuckets(series);
  }

  return [];
};

const pointValue = point => point.count != null ? point.count : (point.rate || 0);

const buildWeekdayBuckets = series => {
  const buckets = WEEKDAY_LABELS.map(label => ({ label, sum: 0, count: 0 }));

  series.forEach(point => {
    const date = new Date(point.date || point.activityDate);
    const jsDay = date.getDay();
    const idx = jsDay === 0 ? 6 : jsDay - 1;
    buckets[idx].sum += pointValue(point);
    buckets[idx].count += 1;
  });

  return buckets.map(({ label, sum, count }) => ({
    label,
    value: count > 0 ? parseFloat((sum / count).toFixed(2)) : 0
  }));
};

const buildWeeklyBuckets = series => {
  const buckets = WEEK_LABELS.map(label => ({ label, sum: 0, count: 0 }));

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  series.forEach(point => {
    const date = new Date(point.date || point.activityDate);
    // Skip dates outside the current month
    if (date.getMonth() !== currentMonth || date.getFullYear() !== currentYear) return;
    const dayOfMonth = date.getDate();
    const weekIdx = Math.min(Math.floor((dayOfMonth - 1) / 7), 3);
    buckets[weekIdx].sum += pointValue(point);
    buckets[weekIdx].count += 1;
  });

  return buckets.map(({ label, sum, count }) => ({
    label,
    value: count > 0 ? parseFloat((sum / count).toFixed(2)) : 0
  }));
};

/**
 * If the API returns pre-grouped chartData array, use it directly.
 * Falls back to buildChartData for raw series.
 */
export const resolveChartData = (apiResponse, preset) => {
  if (apiResponse?.chartData?.length) {
    return apiResponse.chartData;
  }
  return buildChartData(apiResponse?.series || [], preset);
};
